import React, {useCallback} from 'react';

import {SequencePrimerAttributes} from 'biojs/SequencePrimer';
import * as util from 'biojs/Util';
import NoteConstants from 'notebook/notes/NoteConstants';
import {PreviewProps} from 'notebook/notes/previews/PreviewMixin';
import usePreviewMixin from 'notebook/notes/previews/usePreviewMixin';
import {NoteLinkedPrimerPair} from 'notebook/util/NoteUtilConstants';

function getMeltingTemperature(primer: SequencePrimerAttributes): number {
  const bindingBases = primer.bases.slice(primer.overhang_length);
  // Note: nucleotide type is not exposed for the primers, may need to change data if we support RNA primer pairs
  return util.meltingTemperature(bindingBases, util.NucleotideType.DNA);
}

function renderMeltingTemperature(tm: number): string {
  return `${tm.toFixed(1)} \u00B0C`;
}

function getName(item: NoteLinkedPrimerPair): string {
  return `${item.primer_fwd.name} / ${item.primer_rev.name}`;
}

export default function PrimerPairPreview(props: PreviewProps): JSX.Element {
  const {item} = props;
  // @ts-expect-error(2322)
  const primerPair: NoteLinkedPrimerPair = item;
  const getProductLength = useCallback(() => {
    // eslint-disable-next-line camelcase
    const {primer_fwd, primer_rev, sequence_circular, sequence_length} = primerPair;
    let length = util.productLength(
      // eslint-disable-next-line camelcase
      [primer_fwd.start, primer_fwd.end],
      // eslint-disable-next-line camelcase
      [primer_rev.start, primer_rev.end],
      sequence_circular,
      sequence_length
    );
    // eslint-disable-next-line camelcase
    length += primer_fwd.overhang_length + primer_rev.overhang_length;
    return length;
  }, [primerPair]);

  const renderItem = useCallback(() => {
    // eslint-disable-next-line camelcase
    const {primer_fwd, primer_rev} = primerPair;
    const forwardTm = getMeltingTemperature(primer_fwd);
    const reverseTm = getMeltingTemperature(primer_rev);
    // eslint-disable-next-line camelcase
    const primerFwdName = primer_fwd.name;
    // eslint-disable-next-line camelcase
    const primerRevName = primer_rev.name;
    return (
      <dl className="dl-horizontal">
        <dt>Forward Primer</dt>
        <dd>{`${primerFwdName}, ${renderMeltingTemperature(forwardTm)}`}</dd>
        <dt>Reverse Primer</dt>
        <dd>{`${primerRevName}, ${renderMeltingTemperature(reverseTm)}`}</dd>
        <dt>Product Size</dt>
        <dd>{util.formatBaseCount(getProductLength())}</dd>
        <dt>
          T<sub>m</sub>
          {' Difference'}
        </dt>
        <dd>{renderMeltingTemperature(reverseTm - forwardTm)}</dd>
      </dl>
    );
  }, [primerPair, getProductLength]);

  const {render} = usePreviewMixin({
    ...props,
    noteType: NoteConstants.PRIMER_PAIR,
    className: 'note-primer-pair',
    renderName: (): JSX.Element => <>{getName(primerPair)}</>,
    renderItem,
  });

  return render();
}
